import { useLocale } from "../../../hooks/useLocale";
import { msg } from "../../../locales/translate";
import { DatePickerField } from "src/view/conponents/DateRangePicker";
import { Box, HBox, VBox } from "@ui/box/Box";
import Button from "@ui/button/Button";
import Input, { InputBoxStyle, InputFontStyle } from "@ui/input/Input";
import { Select } from "@ui/select/Select";
import Spacer from "@ui/spacer/Spacer";
import { ProgressBar } from "@ui/spinner/ProgressBar";
import { Typo, TypoCaption } from "@ui/text/Typo";
import { pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import { statementsListTotalsRows, statementTotalsCsv, statementTotalsXlsx } from "src/tools/statementTotalsExport";
import { createElement, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { GD } from "src/GD";
import { useAPI } from "src/hooks/useApi";
import { StatementsListPDF, type IStatementsListPdfClient } from "src/tools/pdf/StatementsListPDF";
import { S_STATEMENT_CHANGED, S_STATEMENT_POPUP } from "../../reports/StatementPopup";
import type { IStatementRow, IStatementTotals, IStatementsListData } from "src/types/statement";
import { getStatementTypeLabel } from "src/tools/statements";
import { NoRecords } from "../../providers/NoRecords";
import styled from "styled-components";
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronRight } from "lucide-react";
import { StatementProgressCompact } from "./StatementProgress";
import { statFilterSurface } from "src/view/statView/panelStyles";

type TClientFilterItem = {
	id: number;
	name?: string | null;
	status?: string | null;
};

type TStatementStatusFilterOption = {
	name: string;
	value: "" | "created" | "processing" | "completed" | "rejected";
};

const statementStatusOptions: TStatementStatusFilterOption[] = [
	{ get name() { return msg("all"); }, value: "" },
	{ get name() { return msg("created"); }, value: "created" },
	{ get name() { return msg("processing"); }, value: "processing" },
	{ get name() { return msg("completed"); }, value: "completed" },
	{ get name() { return msg("rejected"); }, value: "rejected" },
];

export interface IStatementsTabProps {
	clientID?: number;
}

const clientStatusColor: Record<string, { bg: string; text: string }> = {
	active: { bg: "#e6f4ea", text: "#1e7e34" },
	blocked: { bg: "#fdecea", text: "#c62828" },
	suspended: { bg: "#fdecea", text: "#c62828" },
	terminated: { bg: "#fdecea", text: "#c62828" },
	pending: { bg: "#fff8e1", text: "#f57f17" },
	review: { bg: "#fff8e1", text: "#f57f17" },
};
const defaultClientStatus = { bg: "#f0f0f0", text: "#555" };
const statementItemLabelSx = { color: "#667085", textTransform: "uppercase", fontSize: "11px", fontWeight: 600, letterSpacing: ".04em", lineHeight: "16px" };
const statementItemBadgeSx = { padding: "2px 8px", border: "1px solid #d0d5dd", borderRadius: "6px", fontSize: "11px", fontWeight: 600, lineHeight: "16px", whiteSpace: "nowrap", flexShrink: 0 };

type TStatementSortKey = "period" | "calculated" | "net";
type TStatementSort = { key: TStatementSortKey; direction: "asc" | "desc" } | null;

const StatementsSurface = styled.div`
	container-type: inline-size;
	min-width: 0;
	font: 13px/1.5 "Euclid Circular B", sans-serif;
	color: #344054;
`;

// One white card with a row per statement: the period leads, the net sits on the right, the rest reads across like a ledger.
const StatementList = styled.div<{ $withClient: boolean }>`
	--columns: ${({ $withClient }) => $withClient
		? "minmax(150px, 1.1fr) minmax(170px, 1.3fr) 190px minmax(140px, 1fr) 160px minmax(130px, .9fr) 20px"
		: "minmax(150px, 1.1fr) 190px minmax(150px, 1fr) 170px minmax(140px, .9fr) 20px"};
	overflow: hidden;
	border: 1px solid #d0d5dd;
	border-radius: 12px;
	background: #fff;
	box-shadow: 0 1px 2px rgba(16,24,40,.05);
	.list-head, .list-row { display: grid; grid-template-columns: var(--columns); align-items: center; gap: 16px; padding: 0 20px; }
	.list-head { min-height: 40px; border-bottom: 1px solid #eaecf0; background: #f9fafb; }
	.list-head > * { color: #667085; font-size: 11px; font-weight: 600; letter-spacing: .04em; line-height: 16px; text-transform: uppercase; white-space: nowrap; }
	.sort { display: inline-flex; align-items: center; gap: 4px; padding: 0; border: 0; background: none; font: inherit; color: inherit; letter-spacing: inherit; text-transform: inherit; cursor: pointer; }
	.sort:hover, .sort[data-active="true"] { color: #182230; }
	.sort svg { flex-shrink: 0; opacity: .55; }
	.sort[data-active="true"] svg { opacity: 1; }
	.right { justify-self: end; text-align: right; }
	.item + .item { border-top: 1px solid #eaecf0; }
	.list-row { width: 100%; min-height: 56px; padding-block: 10px; border: 0; background: transparent; font: inherit; color: inherit; text-align: left; cursor: pointer; }
	.list-row:hover { background: #f8fafc; }
	.list-row:focus-visible { outline: 2px solid #175cd3; outline-offset: -2px; }
	.list-row > * { min-width: 0; }
	.period { color: #182230; font-size: 14px; font-weight: 500; font-variant-numeric: tabular-nums; white-space: nowrap; }
	.type { display: block; color: #98a2b3; font-size: 12px; font-weight: 400; }
	.client { overflow: hidden; color: #182230; text-overflow: ellipsis; white-space: nowrap; }
	.client-id { display: block; color: #98a2b3; font-size: 12px; font-variant-numeric: tabular-nums; }
	.mono { overflow: hidden; color: #667085; font-variant-numeric: tabular-nums; text-overflow: ellipsis; white-space: nowrap; }
	.net { justify-self: end; color: #182230; font-size: 15px; font-weight: 600; font-variant-numeric: tabular-nums; white-space: nowrap; }
	.net small { margin-left: 4px; color: #98a2b3; font-size: 11px; font-weight: 400; }
	.net.empty { color: #98a2b3; font-weight: 400; }
	.chev { color: #98a2b3; }
	.list-row:hover .chev { color: #175cd3; }
	.reason { margin: -4px 20px 0; padding: 0 0 10px; color: #b42318; font-size: 12px; overflow-wrap: anywhere; }
	.cell-label { display: none; }
	/* Narrow: each statement becomes two lines, period and net on top, the rest underneath. */
	@container (max-width: ${({ $withClient }) => $withClient ? "1000px" : "760px"}) {
		.list-head { display: none; }
		.list-row { grid-template-columns: minmax(0, 1fr) auto; gap: 6px 16px; padding-block: 12px; }
		.list-row > .net { grid-row: 1; grid-column: 2; }
		.list-row > .chev { display: none; }
		.list-row > :not(.period):not(.net) { grid-column: 1 / -1; }
		.cell-label { display: inline; color: #98a2b3; }
	}
`;

const statementNetValue = (totals?: Record<string, IStatementTotals> | null) => {
	const currency = Object.keys(totals ?? {}).find(key => key.toUpperCase() === "EUR");
	const value = currency ? totals?.[currency]?.total_net : undefined;
	return typeof value === "number" && Number.isFinite(value) ? value : undefined;
};

const hasDisplayValue = (value: string | number | null | undefined) => value !== null && value !== undefined && String(value).trim() !== "";

const normalizeClientFilterItems = (value: unknown): TClientFilterItem[] => {
	const items = Array.isArray(value)
		? value
		: value && typeof value === "object" && Array.isArray((value as { items?: unknown }).items)
			? (value as { items: unknown[] }).items
			: [];

	return items
		.filter((item): item is TClientFilterItem => typeof item === "object" && item !== null && typeof (item as TClientFilterItem).id === "number")
		.filter(item => item.id > 0 && item.status !== "deleted");
};

const getClientFilterLabel = (client?: TClientFilterItem) => {
	if (!client) return msg("allClients");
	if (hasDisplayValue(client.name)) return String(client.name);
	return msg("templateClient_2fb386d5", { v0: client.id });
};

const getClientStatusLabel = (status?: string | null) => {
	if (!hasDisplayValue(status)) return msg("unknown_b23a6a84");
	return String(status).replace(/_/g, " ");
};

const getClientStatusSx = (status?: string | null) => {
	const normalized = String(status ?? "").toLowerCase();
	if (normalized === "active") return { color: "#166534", backgroundColor: "#dcfce7", border: "1px solid #bbf7d0" };
	if (normalized === "blocked" || normalized === "disabled" || normalized === "deleted" || normalized === "suspended" || normalized === "terminated")
		return { color: "#991b1b", backgroundColor: "#fee2e2", border: "1px solid #fecaca" };
	if (normalized === "pending" || normalized === "created" || normalized === "review") return { color: "#92400e", backgroundColor: "#fef3c7", border: "1px solid #fde68a" };
	return { color: "#334155", backgroundColor: "#e2e8f0", border: "1px solid #cbd5e1" };
};

const sortClientFilterItems = (items: TClientFilterItem[]) =>
	[...items].sort((a, b) => {
		const aActive = String(a.status ?? "").toLowerCase() === "active";
		const bActive = String(b.status ?? "").toLowerCase() === "active";
		if (aActive !== bActive) return aActive ? -1 : 1;
		return getClientFilterLabel(a).localeCompare(getClientFilterLabel(b));
	});

const formatFileDate = (value: Date) => {
	const y = value.getUTCFullYear();
	const m = String(value.getUTCMonth() + 1).padStart(2, "0");
	const d = String(value.getUTCDate()).padStart(2, "0");
	return `${y}-${m}-${d}`;
};

const normalizeUtcDateTime = (value?: string | Date | null) => {
	if (!value) return "";
	const normalized = String(value).trim().replace("T", " ");
	return normalized.replace(/\.\d+Z?$/, "").replace(/Z$/, "").replace(/[+-]\d{2}:?\d{2}$/, "").trim();
};

const parseUtcDate = (value?: string | Date | null) => {
	if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

	const normalized = normalizeUtcDateTime(value);
	if (!normalized) return null;

	const isoValue = normalized.includes(" ") ? normalized.replace(" ", "T") : normalized;
	const date = new Date(`${isoValue}${isoValue.includes("T") ? "Z" : "T00:00:00Z"}`);
	return Number.isNaN(date.getTime()) ? null : date;
};

const formatStatementDate = (value?: string | null) => {
	const date = parseUtcDate(value);
	return date
		? new Intl.DateTimeFormat(undefined, { timeZone: "UTC", year: "numeric", month: "2-digit", day: "2-digit" }).format(date)
		: "-";
};

const formatStatementDateTime = (value?: string | null) => {
	const date = parseUtcDate(value);
	return date
		? new Intl.DateTimeFormat(undefined, {
				timeZone: "UTC",
				year: "numeric",
				month: "2-digit",
				day: "2-digit",
				hour: "2-digit",
				minute: "2-digit",
				second: "2-digit",
				hour12: false,
			}).format(date)
		: "-";
};

const formatStatementPeriod = (start?: string | null, end?: string | null) => {
	if (!start && !end) return "-";
	return `${formatStatementDate(start)} - ${formatStatementDate(end)}`;
};

const statementAmountFormatter = new Intl.NumberFormat(undefined, {
	useGrouping: true,
	minimumFractionDigits: 2,
	maximumFractionDigits: 2,
});

// The net exactly as the statement reports it; a statement without an EUR total shows a dash, never a zero.
const getStatementNetEur = (totals?: Record<string, IStatementTotals> | null) => {
	const value = statementNetValue(totals);
	return value === undefined ? "—" : statementAmountFormatter.format(value);
};

const statementSortValue = (row: IStatementRow, key: TStatementSortKey) => {
	if (key === "net") return statementNetValue(row.totals);
	if (key === "calculated") return parseUtcDate(row.calculated_at)?.getTime();
	return parseUtcDate(row.period_start || row.period_end)?.getTime();
};

// Rows keep the server's order until a column is chosen; rows without a value for that column stay at the end.
const sortStatements = (rows: IStatementRow[], sort: TStatementSort) => {
	if (!sort) return rows;
	const factor = sort.direction === "asc" ? 1 : -1;
	return [...rows].sort((a, b) => {
		const left = statementSortValue(a, sort.key);
		const right = statementSortValue(b, sort.key);
		if (left === undefined || right === undefined) return left === right ? 0 : left === undefined ? 1 : -1;
		return (left - right) * factor;
	});
};

const getStatementClientName = (row: Pick<IStatementRow, "client_name" | "client_id">) => row.client_name ?? msg("templateClient_2fb386d5", { v0: row.client_id });

const groupStatementsForPdf = (rows: IStatementRow[], registrationNumbers: Map<number, string | number | null> = new Map()): IStatementsListPdfClient[] => {
	const grouped = new Map<number, IStatementsListPdfClient>();

	for (const row of rows) {
		const existing = grouped.get(row.client_id);
		const client =
			existing ??
			({
				clientId: row.client_id,
				clientName: getStatementClientName(row),
				registrationNumber: registrationNumbers.get(row.client_id),
				clientStatus: row.client_status,
				statements: [],
			} satisfies IStatementsListPdfClient);

		client.clientName = row.client_name ?? client.clientName;
		client.registrationNumber = registrationNumbers.get(row.client_id) ?? client.registrationNumber;
		client.clientStatus = row.client_status ?? client.clientStatus;
		client.statements.push({
			id: row.id,
			status: row.status,
			period_start: row.period_start,
			period_end: row.period_end,
			totals: row.totals,
		});
		grouped.set(row.client_id, client);
	}

	return Array.from(grouped.values()).sort((a, b) => a.clientName.localeCompare(b.clientName));
};

const ClientFilterControl = ({ client, onClick }: { client?: TClientFilterItem; onClick: () => void }) => { useLocale(); return ((
	<Box sx={{ position: "relative", minWidth: "190px", maxWidth: "260px" }}>
		<TypoCaption block>{msg("client_b886ce22")}</TypoCaption>
		<Spacer size="3px" />
		<HBox
			onClick={event => {
				event?.preventDefault();
				event?.stopPropagation();
				onClick();
			}}
			sx={{
				alignItems: "center",
				minWidth: 0,
				...InputBoxStyle(false),
				borderRadius: "4px",
				cursor: "pointer",
				overflow: "hidden",
				position: "relative",
			}}>
			<Box sx={{ ...InputFontStyle("small"), overflow: "hidden", flex: "1 1 auto", minWidth: 0 }}>
				<Typo sx={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", color: "#001a41" }}>
					{getClientFilterLabel(client)}
				</Typo>
			</Box>
			<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, width: "30px", "&>svg": { pointerEvents: "none", height: "24px", width: "24px" } }}>
				<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M17 9V11L12 16L7 11V9L12 14L17 9Z" fill="#001a41" fillOpacity="0.8" />
				</svg>
			</Box>
		</HBox>
	</Box>
)); };

const ClientFilterPopup = ({
	open,
	items,
	busy,
	error,
	search,
	selectedClient,
	onSearchChange,
	onSelect,
	onClose,
}: {
	open: boolean;
	items: TClientFilterItem[];
	busy: boolean;
	error?: string;
	search: { search: string };
	selectedClient?: TClientFilterItem;
	onSearchChange: (value: string) => void;
	onSelect: (client?: TClientFilterItem) => void;
	onClose: () => void;
}) => {
	useLocale();
	if (!open) return null;

	const searchValue = search.search.trim().toLowerCase();
	const sortedItems = sortClientFilterItems(items);
	const filteredItems = searchValue
		? sortedItems.filter(client => {
				const haystack = [client.name, client.status, client.id].filter(hasDisplayValue).join(" ").toLowerCase();
				return haystack.includes(searchValue);
			})
		: sortedItems;

	return createPortal(
		<Box
			onClick={onClose}
			sx={{
				position: "fixed",
				inset: 0,
				zIndex: 10000,
				backgroundColor: "rgba(15,23,42,0.22)",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				padding: "20px",
				boxSizing: "border-box",
			}}>
			<VBox
				onClick={event => event?.stopPropagation()}
				sx={{
					width: "min(520px, 100%)",
					maxHeight: "75vh",
					backgroundColor: "#fff",
					borderRadius: "14px",
					boxShadow: "0 22px 60px rgba(15,23,42,0.28), 0 6px 20px rgba(15,23,42,0.16)",
					overflow: "hidden",
				}}>
				<HBox sx={{ alignItems: "center", gap: "10px", padding: "16px 18px", borderBottom: "1px solid #f1f5f9" }}>
					<Typo variant="medium" bold sx={{ fontSize: "15px", color: "#1e293b" }}>{msg("selectClient")}</Typo>
					<Spacer grow />
					<Button onClick={onClose} title={msg("close")} variant="secondarySmall" />
				</HBox>

				<VBox sx={{ padding: "14px 18px", gap: "12px", minHeight: 0 }}>
					<Input size="small" type="search" placeholder={msg("search_7f553822")} name="search" onChange={onSearchChange} data={search} />

					<VBox sx={{ overflow: "auto", maxHeight: "52vh", gap: "6px" }}>
						<HBox
							onClick={() => onSelect(undefined)}
							sx={{
								padding: "10px 12px",
								border: "1px solid #cbd5e1",
								borderRadius: "8px",
								cursor: "pointer",
								backgroundColor: !selectedClient ? "#eef4ff" : "#fff",
								"&:hover": { backgroundColor: "#f1f5f9" },
							}}>
							<Typo variant="medium" sx={{ color: "#1e293b", fontSize: "13px", fontWeight: 600 }}>{msg("allClients")}</Typo>
						</HBox>

						{busy && <Typo variant="medium" sx={{ color: "#94a3b8", padding: "12px" }}>{msg("loading")}</Typo>}
						{error && !busy && <Typo variant="medium" sx={{ color: "#dc2626", padding: "12px" }}>{error}</Typo>}
						{!busy && !error && filteredItems.length === 0 && <Typo variant="medium" sx={{ color: "#94a3b8", padding: "12px" }}>{msg("noClients")}</Typo>}

						{!busy &&
							!error &&
							filteredItems.map(client => (
								<HBox
									key={client.id}
									onClick={() => onSelect(client)}
									sx={{
										padding: "10px 12px",
										border: "1px solid #cbd5e1",
										borderRadius: "8px",
										cursor: "pointer",
										alignItems: "center",
										gap: "12px",
										backgroundColor: selectedClient?.id === client.id ? "#eef4ff" : "#fff",
										"&:hover": { backgroundColor: "#f8fafc", borderColor: "#cbd5e1" },
									}}>
									<Typo variant="medium" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: "13px", color: "#0f172a", fontWeight: 600 }}>
										{getClientFilterLabel(client)}
									</Typo>
									<Spacer grow />
									<Typo
										variant="caption"
										sx={{ flexShrink: 0, borderRadius: "6px", padding: "4px 8px", fontSize: "11px", textTransform: "capitalize", whiteSpace: "nowrap", ...getClientStatusSx(client.status) }}>
										{getClientStatusLabel(client.status)}
									</Typo>
									<Typo
										variant="caption"
										sx={{ flexShrink: 0, color: "#334155", backgroundColor: "#e2e8f0", borderRadius: "6px", padding: "4px 8px", fontSize: "11px", fontVariantNumeric: "tabular-nums" }}>
										#{client.id}
									</Typo>
								</HBox>
							))}
					</VBox>
				</VBox>
			</VBox>
		</Box>,
		document.body,
	);
};

export const StatementsTab = ({ clientID }: IStatementsTabProps) => {
	useLocale();
	const [date, setDate] = useState("");
	const [clientFilter, setClientFilter] = useState<TClientFilterItem | undefined>();
	const [clientFilterItems, setClientFilterItems] = useState<TClientFilterItem[]>([]);
	const [clientFilterOpen, setClientFilterOpen] = useState(false);
	const [clientFilterBusy, setClientFilterBusy] = useState(false);
	const [clientFilterError, setClientFilterError] = useState<string | undefined>();
	const [clientSearch, setClientSearch] = useState<{ search: string }>({ search: "" });
	const [statusFilter, setStatusFilter] = useState<TStatementStatusFilterOption>(statementStatusOptions[0]);
	const [pdfBusy, setPdfBusy] = useState(false);
	const [exportFormat, setExportFormat] = useState<"csv" | "xlsx" | null>(null);
	const [exportError, setExportError] = useState<string>();
	const [sort, setSort] = useState<TStatementSort>(null);
	const getRequestData = (dateValue = date, client = clientFilter, status = statusFilter) => ({
		...(dateValue ? { date: dateValue } : {}),
		...(clientID ? { client_id: clientID } : client?.id ? { client_id: client.id } : {}),
		...(status.value ? { status: status.value } : {}),
	});
	const [data, request, busy] = useAPI<IStatementsListData>({
		method: "statements/list",
		data: getRequestData(),
	});

	const loadStatements = (dateValue = date, client = clientFilter, status = statusFilter) => {
		request({ method: "statements/list", data: getRequestData(dateValue, client, status) });
	};

	const loadStatementForPdf = async (row: IStatementRow): Promise<IStatementRow> => {
		if (row.totals && Object.keys(row.totals).length > 0 && row.client_name) return row;

		const res = await GD.REQ_API.request({
			method: "statements/get",
			data: { id: row.id },
		});
		const statementData = res?.data;

		return {
			...row,
			client_name: statementData?.data?.client_name ?? row.client_name,
			client_status: statementData?.data?.client_status ?? row.client_status,
			totals: statementData?.totals ?? row.totals ?? null,
		};
	};

	const loadRegistrationNumbersForPdf = async (rows: IStatementRow[]) => {
		const clientIds = Array.from(new Set(rows.map(row => row.client_id).filter(Boolean)));
		const entries = await Promise.all(
			clientIds.map(async clientId => {
				const res = await GD.REQ_CLIENT.request({ clientID: clientId });
				return [clientId, res?.data?.registration_number ?? null] as const;
			}),
		);
		return new Map<number, string | number | null>(entries);
	};

	const handleDownloadTable = async (format: "csv" | "xlsx") => {
		if (rows.length === 0 || busy || pdfBusy || exportFormat) return;
		setExportFormat(format);
		setExportError(undefined);
		try {
			const detailedRows: IStatementRow[] = [];
			for (const row of rows) {
				if (row.totals && row.client_name && row.calculated_at !== undefined) {
					detailedRows.push(row);
					continue;
				}
				const response: IAPIResponse<IStatementRow> = await GD.REQ_API.request({ method: "statements/get", data: { id: row.id } });
				if (response.error || !response.data || response.data.id !== row.id) {
					throw new Error(response.error?.message ?? msg("templateUnableToLoadStatement_02eee489", { v0: row.id }));
				}
				detailedRows.push({ ...row, ...response.data, client_name: response.data.data?.client_name ?? response.data.client_name ?? row.client_name });
			}
			const table = statementsListTotalsRows(detailedRows);
			const blob = format === "csv"
				? new Blob([statementTotalsCsv(table)], { type: "text/csv;charset=utf-8" })
				: new Blob([new Uint8Array(statementTotalsXlsx(table)).buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
			const name = ["statements", clientID ? `client_${clientID}` : "list", formatFileDate(new Date())].join("_");
			saveAs(blob, `${name}.${format}`);
		} catch (error) {
			setExportError(error instanceof Error ? error.message : msg("unableToExportStatementsPleaseTryAgain"));
		} finally {
			setExportFormat(null);
		}
	};

	const handleDownloadPdf = async () => {
		if (rows.length === 0 || pdfBusy) return;

		setPdfBusy(true);
		try {
			const detailedRows = await Promise.all(rows.map(loadStatementForPdf));
			const registrationNumbers = await loadRegistrationNumbersForPdf(detailedRows);
			const clients = groupStatementsForPdf(detailedRows, registrationNumbers);
			const blob = await pdf(createElement(StatementsListPDF, { clients, isPayswix: detailedRows.some(row => row.project_id === 3) }) as any).toBlob();
			const url = URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			const nameParts = ["statements"];
			if (date) nameParts.push(date);
			if (clientID) nameParts.push(`client_${clientID}`);
			else if (clientFilter?.id) nameParts.push(`client_${clientFilter.id}`);
			if (statusFilter.value) nameParts.push(statusFilter.value);
			nameParts.push(formatFileDate(new Date()));
			a.download = `${nameParts.join("_")}.pdf`;
			a.click();
			URL.revokeObjectURL(url);
		} catch (error) {
			console.error("Statements PDF generation failed:", error);
		} finally {
			setPdfBusy(false);
		}
	};

	useEffect(() => {
		const handler = S_STATEMENT_CHANGED.subscribe(() => {
			loadStatements();
		});
		return () => {
			S_STATEMENT_CHANGED.unsubscribe(handler);
		};
	}, [date, clientFilter?.id, statusFilter.value, request]);

	const openClientFilter = async () => {
		setClientFilterOpen(true);
		setClientSearch({ search: "" });
		setClientFilterBusy(true);
		setClientFilterError(undefined);

		try {
			const res = await GD.REQ_CLIENTS.request({});
			if (res?.error) {
				setClientFilterError(res.error.message ?? msg("unableToLoadClients"));
				setClientFilterItems([]);
				return;
			}
			setClientFilterItems(normalizeClientFilterItems(res?.data));
		} finally {
			setClientFilterBusy(false);
		}
	};

	const selectClientFilter = (client?: TClientFilterItem) => {
		setClientFilter(client);
		setClientFilterOpen(false);
	};

	const rows: IStatementRow[] = data?.data?.rows ?? [];
	const noData = !busy && data && rows.length === 0;
	const showClientFilter = !clientID;
	const sortedRows = sortStatements(rows, sort);
	// First click sorts newest or largest first, the second flips it, the third returns to the server's order.
	const toggleSort = (key: TStatementSortKey) => setSort(current => current?.key !== key ? { key, direction: "desc" } : current.direction === "desc" ? { key, direction: "asc" } : null);
	const sortHeader = (key: TStatementSortKey, label: string, className?: string) => {
		const active = sort?.key === key;
		const Icon = !active ? ArrowUpDown : sort.direction === "asc" ? ArrowUp : ArrowDown;
		return <span className={className} aria-sort={active ? sort.direction === "asc" ? "ascending" : "descending" : "none"} role="columnheader">
			<button type="button" className="sort" data-active={active} title={`${msg("sortBy")}: ${label}`} onClick={() => toggleSort(key)}>
				{label}<Icon size={12} aria-hidden="true" />
			</button>
		</span>;
	};

	return (
		<StatementsSurface>
			<ProgressBar busy={busy} />
			<HBox gap="12px" sx={{ ...statFilterSurface, alignItems: "end", flexWrap: "wrap", padding: "14px 16px" }}>
				<DatePickerField style={{ width: "190px" }} label={msg("date_99c40ab4")} value={date} allowClear placeholder={msg("anyDate")} onChange={setDate} />
				<Select<TStatementStatusFilterOption>
					sx={{ minWidth: "150px", maxWidth: "190px" }}
					size="small"
					label={msg("status")}
					value={statusFilter}
					values={statementStatusOptions}
					onChange={value => setStatusFilter(statementStatusOptions.find(option => option.value === (value as unknown as string)) ?? statementStatusOptions[0])}
				/>
				{showClientFilter && <ClientFilterControl client={clientFilter} onClick={openClientFilter} />}
				<Button title={msg("show")} variant="primarySmall" onClick={() => loadStatements()} />
				<Spacer grow />
				{(["csv", "xlsx"] as const).map(format => (
					<Button key={format} title={exportFormat === format ? msg("generating") : msg("templateDownload_ac3a0cac", { v0: format === "csv" ? "CSV" : "Excel" })} variant="secondarySmall" onClick={() => handleDownloadTable(format)} disabled={pdfBusy || !!exportFormat || busy || rows.length === 0} />
				))}
				<Button title={pdfBusy ? msg("generating") : msg("downloadPdf")} variant="secondarySmall" onClick={handleDownloadPdf} disabled={pdfBusy || !!exportFormat || busy || rows.length === 0} />
			</HBox>
			{exportError && <div role="alert"><Typo sx={{ color: "#b42318", marginTop: "8px" }}>{exportError}</Typo></div>}
			<ClientFilterPopup
				open={clientFilterOpen}
				items={clientFilterItems}
				busy={clientFilterBusy}
				error={clientFilterError}
				search={clientSearch}
				selectedClient={clientFilter}
				onSearchChange={value => setClientSearch({ search: value })}
				onSelect={selectClientFilter}
				onClose={() => setClientFilterOpen(false)}
			/>

			{noData && (
				<>
					<Spacer size="10px" />
					<NoRecords line1={msg("ui_69dfe18b1b")} line2={msg("ui_ac9c99cdb7")} />
				</>
			)}
			<Box sx={{ marginTop: "20px", minWidth: 0 }}>
				<HBox gap="8px" sx={{ alignItems: "center", marginBottom: "8px" }}>
					<Typo sx={statementItemLabelSx}>{msg("statements")}</Typo>
					<Box sx={{ ...statementItemBadgeSx, color: "#667085", backgroundColor: "#fff" }}>{rows.length}</Box>
				</HBox>
				{rows.length > 0 && <StatementList $withClient={!clientID} role="table" aria-label={msg("statements")}>
					<div className="list-head" role="row">
						{sortHeader("period", msg("period_6e795d4d"))}
						{!clientID && <span role="columnheader">{msg("client")}</span>}
						<span role="columnheader">{msg("status")}</span>
						<span role="columnheader">{msg("statementId")}</span>
						{sortHeader("calculated", `${msg("calculated")} · UTC`)}
						{sortHeader("net", `${msg("net")} · EUR`, "right")}
						<span aria-hidden="true" />
					</div>
					{sortedRows.map(row => {
						const isDaily = row.type?.trim().toLowerCase() === "daily";
						const net = getStatementNetEur(row.totals);
						return (
							<div className="item" key={row.id} role="row">
								<button type="button" className="list-row" aria-label={`${msg("open")} #${row.id}`} onClick={() => S_STATEMENT_POPUP.invoke(row)}>
									<span className="period" role="cell">
										{isDaily ? formatStatementDate(row.period_start || row.period_end) : formatStatementPeriod(row.period_start, row.period_end)}
										{row.type?.trim() && <span className="type">{getStatementTypeLabel(row.type)}</span>}
									</span>
									{!clientID && (
										<span role="cell" style={{ minWidth: 0 }}>
											<span className="client" style={{ display: "block" }}>
												{getStatementClientName(row)}
												{row.client_status && (() => {
													const c = clientStatusColor[row.client_status.toLowerCase()] ?? defaultClientStatus;
													return <Box sx={{ ...statementItemBadgeSx, display: "inline-block", marginLeft: "8px", border: 0, backgroundColor: c.bg, color: c.text }}>{row.client_status}</Box>;
												})()}
											</span>
											<span className="client-id">{msg("id")}{row.client_id}</span>
										</span>
									)}
									<span role="cell">
										<StatementProgressCompact status={row.status} label={statementStatusOptions.find(option => option.value === row.status.toLowerCase())?.name ?? row.status} />
									</span>
									<span className="mono" role="cell" title={`#${row.id}`}><span className="cell-label">{msg("statementId")}: </span>#{row.id}</span>
									<span className="mono" role="cell"><span className="cell-label">{msg("calculated")}: </span>{formatStatementDateTime(row.calculated_at)}</span>
									<span className={`net${net === "—" ? " empty" : ""}`} role="cell">{net}{net !== "—" && <small>EUR</small>}</span>
									<ChevronRight className="chev" size={18} aria-hidden="true" />
								</button>
								{row.rejected_reason && <div className="reason">{msg("reason")}{row.rejected_reason}</div>}
							</div>
						);
					})}
				</StatementList>}
			</Box>
		</StatementsSurface>
	);
};
